// frontend/src/components/InvoiceImportConfirm.jsx
// Modale non-bloquante affichée quand l'OCR détecte des incohérences avec le workspace

import { useState } from "react";

/**
 * Affiche les warnings OCR et demande à l'utilisateur s'il veut continuer.
 *
 * Props :
 *   warnings   — tableau de warnings retourné par InvoiceOcrParser.parseWithAI()
 *   invoiceRef — numéro ou identifiant de la facture (pour l'affichage)
 *   onConfirm  — () => void  → l'utilisateur accepte et continue l'import
 *   onCancel   — () => void  → l'utilisateur annule
 *
 * Usage :
 *   const result = await InvoiceOcrParser.parseWithAI(ocrText, { workspace });
 *   if (result.warnings?.length) {
 *     <InvoiceImportConfirm
 *       warnings={result.warnings}
 *       invoiceRef={result.supplierInvoiceNumber}
 *       onConfirm={() => importInvoice(result)}
 *       onCancel={() => setShowModal(false)}
 *     />
 *   }
 */

const ICONS = {
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ width: 16, height: 16 }}>
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  invoice: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 40, height: 40 }}>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
};

const WARN_LABELS = {
  CLIENT_MF_MISMATCH:  'Matricule fiscal client différent',
  CLIENT_MF_NOT_FOUND: 'Matricule fiscal client introuvable',
  CLIENT_NAME_MISMATCH:'Nom client potentiellement différent',
};

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(10,12,20,0.72)',
    backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '16px',
    animation: 'fadeIn 0.18s ease',
  },
  modal: {
    background: '#0f1117',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
    overflow: 'hidden',
    animation: 'slideUp 0.22s cubic-bezier(0.34,1.56,0.64,1)',
  },
  header: {
    padding: '20px 24px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', alignItems: 'flex-start', gap: '14px',
  },
  iconWrap: {
    width: 56, height: 56, borderRadius: '12px',
    background: 'rgba(245,158,11,0.12)',
    border: '1px solid rgba(245,158,11,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#f59e0b', flexShrink: 0,
  },
  headerText: { flex: 1 },
  title: { color: '#f8fafc', fontSize: '16px', fontWeight: 700, margin: '0 0 4px', lineHeight: 1.3, fontFamily: 'system-ui,sans-serif' },
  subtitle: { color: '#64748b', fontSize: '13px', margin: 0, fontFamily: 'system-ui,sans-serif' },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#475569', padding: '4px', borderRadius: '6px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'color 0.15s, background 0.15s',
  },
  body: { padding: '20px 24px' },
  warningList: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' },
  warningItem: (level) => ({
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    padding: '12px 14px', borderRadius: '10px',
    background: level === 'warning' ? 'rgba(245,158,11,0.07)' : 'rgba(59,130,246,0.07)',
    border: `1px solid ${level === 'warning' ? 'rgba(245,158,11,0.2)' : 'rgba(59,130,246,0.2)'}`,
  }),
  warningIcon: (level) => ({
    color: level === 'warning' ? '#f59e0b' : '#60a5fa',
    flexShrink: 0, marginTop: '1px',
  }),
  warningContent: { flex: 1 },
  warningLabel: (level) => ({
    fontSize: '12px', fontWeight: 600, letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: level === 'warning' ? '#f59e0b' : '#60a5fa',
    marginBottom: '3px', fontFamily: 'system-ui,sans-serif',
  }),
  warningMsg: { color: '#94a3b8', fontSize: '13px', lineHeight: 1.5, margin: 0, fontFamily: 'system-ui,sans-serif' },
  diffRow: { display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' },
  diffChip: (type) => ({
    fontSize: '11px', padding: '3px 8px', borderRadius: '20px',
    fontFamily: 'monospace',
    background: type === 'expected' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
    color: type === 'expected' ? '#86efac' : '#fca5a5',
    border: `1px solid ${type === 'expected' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
  }),
  question: {
    color: '#e2e8f0', fontSize: '14px', fontWeight: 500,
    textAlign: 'center', marginBottom: '16px', fontFamily: 'system-ui,sans-serif',
  },
  actions: { display: 'flex', gap: '10px' },
  btnCancel: {
    flex: 1, padding: '11px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#94a3b8', cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
    transition: 'all 0.15s',
  },
  btnConfirm: {
    flex: 1, padding: '11px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
    border: 'none', color: '#0f1117', cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
    transition: 'all 0.15s',
    boxShadow: '0 4px 14px rgba(245,158,11,0.3)',
  },
};

export default function InvoiceImportConfirm({ warnings = [], invoiceRef, onConfirm, onCancel }) {
  const [hoveredBtn, setHoveredBtn] = useState(null);

  if (!warnings.length) return null;

  const hasBlockingWarning = warnings.some(w => w.level === 'warning');

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(24px) scale(0.97); opacity:0 } to { transform:translateY(0) scale(1); opacity:1 } }
      `}</style>

      <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onCancel?.()}>
        <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="invoice-warn-title">

          {/* Header */}
          <div style={styles.header}>
            <div style={styles.iconWrap}>{ICONS.invoice}</div>
            <div style={styles.headerText}>
              <p id="invoice-warn-title" style={styles.title}>Vérification requise</p>
              <p style={styles.subtitle}>
                {invoiceRef ? `Facture N° ${invoiceRef} · ` : ''}
                {warnings.length} anomalie{warnings.length > 1 ? 's' : ''} détectée{warnings.length > 1 ? 's' : ''}
              </p>
            </div>
            <button
              style={{
                ...styles.closeBtn,
                ...(hoveredBtn === 'close' ? { color: '#e2e8f0', background: 'rgba(255,255,255,0.06)' } : {}),
              }}
              onMouseEnter={() => setHoveredBtn('close')}
              onMouseLeave={() => setHoveredBtn(null)}
              onClick={onCancel}
              aria-label="Fermer"
            >
              {ICONS.close}
            </button>
          </div>

          {/* Body */}
          <div style={styles.body}>
            <div style={styles.warningList}>
              {warnings.map((w, i) => (
                <div key={i} style={styles.warningItem(w.level)}>
                  <span style={styles.warningIcon(w.level)}>
                    {w.level === 'warning' ? ICONS.warning : ICONS.info}
                  </span>
                  <div style={styles.warningContent}>
                    <div style={styles.warningLabel(w.level)}>
                      {WARN_LABELS[w.code] || w.code}
                    </div>
                    <p style={styles.warningMsg}>{w.message}</p>

                    {/* Comparaison attendu / trouvé */}
                    {(w.expected || w.found) && (
                      <div style={styles.diffRow}>
                        {w.expected && (
                          <span style={styles.diffChip('expected')}>
                            ✓ Attendu : {w.expected}
                          </span>
                        )}
                        {w.found && (
                          <span style={styles.diffChip('found')}>
                            ✗ Facture : {w.found}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Question */}
            <p style={styles.question}>
              {hasBlockingWarning
                ? 'Cette facture semble ne pas appartenir à votre workspace. Voulez-vous quand même continuer l\'import ?'
                : 'Souhaitez-vous continuer l\'import malgré ces avertissements ?'}
            </p>

            {/* Actions */}
            <div style={styles.actions}>
              <button
                style={{
                  ...styles.btnCancel,
                  ...(hoveredBtn === 'cancel' ? { color: '#e2e8f0', background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.15)' } : {}),
                }}
                onMouseEnter={() => setHoveredBtn('cancel')}
                onMouseLeave={() => setHoveredBtn(null)}
                onClick={onCancel}
              >
                Annuler l'import
              </button>
              <button
                style={{
                  ...styles.btnConfirm,
                  ...(hoveredBtn === 'confirm' ? { filter: 'brightness(1.1)', boxShadow: '0 6px 20px rgba(245,158,11,0.4)' } : {}),
                }}
                onMouseEnter={() => setHoveredBtn('confirm')}
                onMouseLeave={() => setHoveredBtn(null)}
                onClick={onConfirm}
              >
                Continuer quand même
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
